import {useFilesPageInfo} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {useCurrentRepository, type Repository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {dismissRepositoryNoticePathPath, newRulesetPath, repoOverviewUrl} from '@github-ui/paths'
import {useClientValue} from '@github-ui/use-client-value'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {GitBranchIcon} from '@primer/octicons-react'
import {Link, Banner} from '@primer/react'
import {useState} from 'react'

export function ProtectBranchBanner({helpUrl}: {helpUrl: string}) {
  const [hidden, setHidden] = useState<boolean>(false)

  const {refInfo} = useFilesPageInfo()
  const repo = useCurrentRepository()
  const docPath = `/repositories/configuring-branches-and-merges-in-your-repository/managing-rulesets/about-rulesets`
  const documentationHref = `${helpUrl}${docPath}`
  const currentUser = useCurrentUser()

  const onDismissProtectBranchBanner = () => {
    if (!currentUser) return
    const dismissPath = dismissRepositoryNoticePathPath({login: currentUser.login})
    const form = new FormData()
    form.append('_method', 'delete')
    form.append('repository_id', repo.id.toString())
    form.append('notice_name', 'sculk_protect_this_branch')
    verifiedFetch(dismissPath, {
      method: 'POST',
      body: form,
    })
    setHidden(true)
  }

  const [isSSR] = useClientValue(() => false, true, [])

  const learnMoreRulesetAnalytics = getAnalytics(
    'click_to_learn_more_about_rulesets',
    `ref_cta:learn_more_about_rulesets`,
    repo,
    isSSR,
  )
  const protectBranchAnalytics = getAnalytics('click_to_add_a_rule', 'ref_cta:protect_this_branch', repo, isSSR)
  const dismissAnalytics = getAnalytics('click_to_dismiss', 'ref_cta:dismiss', repo, isSSR)
  const protectThisBranchHref = newRulesetPath({owner: repo.ownerLogin, repo: repo.name})

  if (hidden) return null

  return (
    <Banner
      icon={<GitBranchIcon />}
      title={`Your ${refInfo.name} branch isn't protected`}
      description={
        <span>
          Protect this branch from force pushing or deletion, or require status checks before merging.&nbsp;
          <Link inline href={documentationHref} data-analytics-event={learnMoreRulesetAnalytics}>
            View documentation.
          </Link>
        </span>
      }
      primaryAction={
        <Banner.PrimaryAction as="a" href={protectThisBranchHref} data-analytics-event={protectBranchAnalytics}>
          Protect this branch
        </Banner.PrimaryAction>
      }
      secondaryAction={
        <Banner.SecondaryAction onClick={onDismissProtectBranchBanner} data-analytics-event={dismissAnalytics}>
          Dismiss
        </Banner.SecondaryAction>
      }
    />
  )
}

function getAnalytics(action: string, label: string, repo: Repository, isSSR: boolean) {
  return JSON.stringify({
    category: 'Suggestions',
    action,
    label: `ref_page:${
      isSSR ? `https://github.com${repoOverviewUrl(repo)}` : window.location
    };${label};ref_loc:repo files listing;`,
  })
}

try{ ProtectBranchBanner.displayName ||= 'ProtectBranchBanner' } catch {}